import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardCheck, FlaskConical, ShieldCheck, Ship, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionHeading, LearnMore } from '../components/Section';
import { serviceCatalog, servicesPageContent } from '../data/mock';
import { useServices, usePage } from '../lib/cms';
import SEO from '../lib/SEO';

const iconMap = { ClipboardCheck, FlaskConical, ShieldCheck, Ship };

export default function Services() {
  const services = useServices();
  const { content: page } = usePage('services');
  const hero = page.hero || {};

  const servicesLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: services.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: { '@type': 'Service', name: s.title, description: s.tagline, url: `/services/${s.slug}` },
    })),
  };

  return (
    <div className="font-sans-body">
      <SEO jsonLd={servicesLd} />
      <section className="relative overflow-hidden">
        <div className="container-aqc relative py-16 lg:py-20 text-center">
          <span className="eyebrow">{hero.eyebrow || 'Our Services'}</span>
          <h1 className="mt-5 font-serif text-[2.4rem] sm:text-[3rem] leading-[1.08] font-semibold text-aqc-navy max-w-4xl mx-auto">
            {hero.title || 'End-to-End Quality Assurance.'} {hero.italic && <span className="italic text-aqc-red">{hero.italic}</span>}
          </h1>
          <p className="mt-6 text-aqc-muted max-w-2xl mx-auto">
            {hero.subtitle || 'Four core capabilities. Twelve service types. One coordinated programme tailored to your product, your factories, and your risk appetite.'}
          </p>
        </div>
      </section>

      {/* Detailed intro */}
      <section className="py-16">
        <div className="container-aqc grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-serif text-3xl text-aqc-navy">{servicesPageContent.intro.title}</h2>
            <p className="mt-4 text-aqc-ink/85 leading-relaxed">{servicesPageContent.intro.body}</p>
          </div>
          <div className="bg-white border border-aqc-line rounded-2xl p-7 shadow-sm">
            <h3 className="font-serif text-2xl text-aqc-navy">{servicesPageContent.whyIndependent.title}</h3>
            <p className="mt-3 text-aqc-ink/85 leading-relaxed">{servicesPageContent.whyIndependent.body}</p>
            <ul className="mt-4 space-y-2">
              {servicesPageContent.whyIndependent.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[0.95rem] text-aqc-ink/85">
                  <CheckCircle2 size={16} className="text-aqc-red mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-aqc-cream">
        <div className="container-aqc">
          <SectionHeading eyebrow="Process" title={servicesPageContent.process.title} />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {servicesPageContent.process.steps.map((step, i) => (
              <div key={step.title} className="relative bg-white border border-aqc-line rounded-2xl p-6">
                <div className="absolute -top-4 left-6 h-9 w-9 rounded-full bg-aqc-red text-white text-sm font-semibold flex items-center justify-center">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="mt-3 font-serif text-lg text-aqc-navy">{step.title}</h3>
                <p className="mt-2 text-sm text-aqc-muted leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core services cards (existing) */}
      <section className="py-16">
        <div className="container-aqc">
          <SectionHeading eyebrow="Core Services" title="Four capabilities that cover your entire production cycle." />
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || ClipboardCheck;
              return (
                <div key={s.slug} className="bg-white rounded-2xl border border-aqc-line overflow-hidden card-lift">
                  <div className="relative h-56">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-aqc-navy/40 to-transparent" />
                    <div className="absolute bottom-4 left-4 h-11 w-11 rounded-full bg-aqc-navy text-white flex items-center justify-center shadow-lg">
                      <Icon size={18} />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl text-aqc-navy">{s.title}</h3>
                    <p className="mt-1.5 text-aqc-muted text-[0.95rem]">{s.tagline}</p>
                    <ul className="mt-4 space-y-2">
                      {(s.bullets || []).slice(0, 3).map((b) => (
                        <li key={b} className="flex items-start gap-2 text-[0.9rem] text-aqc-ink/80">
                          <CheckCircle2 size={16} className="text-aqc-red mt-0.5 shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5"><LearnMore to={`/services/${s.slug}`} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documentation + Benefits */}
      <section className="py-16 bg-aqc-cream">
        <div className="container-aqc grid lg:grid-cols-2 gap-5">
          <div className="bg-white border border-aqc-line rounded-2xl p-7">
            <h3 className="font-serif text-2xl text-aqc-navy">{servicesPageContent.documentation.title}</h3>
            <ul className="mt-4 space-y-2">
              {servicesPageContent.documentation.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[0.95rem] text-aqc-ink/85">
                  <CheckCircle2 size={16} className="text-aqc-red mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-aqc-line rounded-2xl p-7">
            <h3 className="font-serif text-2xl text-aqc-navy">{servicesPageContent.benefits.title}</h3>
            <ul className="mt-4 space-y-2">
              {servicesPageContent.benefits.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[0.95rem] text-aqc-ink/85">
                  <CheckCircle2 size={16} className="text-aqc-red mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Full catalog */}
      <section className="py-16">
        <div className="container-aqc">
          <SectionHeading eyebrow="Full Service Catalog" title="Twelve service types. One coordinator." subtitle="Mix and match to build the right programme for your product and risk profile." />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {serviceCatalog.map((item) => (
              <div key={item} className="bg-white border border-aqc-line rounded-xl px-4 py-4 text-[0.92rem] font-medium text-aqc-navy hover:border-aqc-red hover:shadow-md transition-all flex items-center gap-2">
                <span className="h-2 w-2 rotate-45 bg-aqc-red" /> {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to request */}
      <section className="py-16 bg-aqc-cream">
        <div className="container-aqc max-w-3xl text-center">
          <span className="eyebrow">How to Request</span>
          <h2 className="mt-4 font-serif text-3xl text-aqc-navy">{servicesPageContent.howToRequest.title}</h2>
          <p className="mt-4 text-aqc-ink/85 leading-relaxed">{servicesPageContent.howToRequest.body}</p>
          <div className="mt-6">
            <Link to="/contact"><Button className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white h-11 px-8">Request a service <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-aqc-navy text-white">
        <div className="container-aqc text-center">
          <h2 className="font-serif text-3xl">Not sure which service you need?</h2>
          <p className="mt-3 text-white/75 max-w-xl mx-auto">Speak to a coordinator and we’ll build a programme that fits your product and factory.</p>
          <div className="mt-7">
            <Link to="/contact"><Button className="bg-aqc-red hover:brightness-110 text-white h-11 px-8">Contact Us <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
